import { Typography, Grid, Button, AppBar, Dialog, IconButton, Toolbar, CircularProgress } from "@mui/material";
import { axisClasses } from '@mui/x-charts';
import { BarChart } from '@mui/x-charts/BarChart';
import { useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import { DrillDownParams } from "./CommonInterfaces";
import { Item, percentage, round } from "./CommonComponents";
import DrillDownTable from "./DrillDownTable";
import CloseIcon from '@mui/icons-material/Close';
import RetractionsForOpenAlexAuthorDialog from "./drilldowns/RetractionsForOpenAlexAuthorDialog";
import JournalSummaryTable from "./JournalSummaryTable";


interface PublisherAnnualMetricsRequestResult {
    results: PublisherAnnualMetrics[]
}

interface PublisherAnnualMetrics {
    publisherId: string,
    publisher: string,
    publicationYear: number,
    totalAnalysed: number,
    retractionsByPublicationYear: number,
    numberOfHighRiskArticles: number,
    numberOfMediumRiskArticles: number,
    numberOfLowRiskArticles: number
}



interface PublisherDashboardProps {
    publisherId: string
}

function PublisherDashboard({ publisherId }: PublisherDashboardProps) {
    const START_YEAR = 2014;
    const END_YEAR = 2025;
    const [totalAnalized, setTotalAnalized] = useState<number>(0)
    const [totalRetractions, setTotalRetractions] = useState<number>(0)
    const [highRiskArticles, setHighRiskArticles] = useState<number>(0)
    const [mediumRiskArticles, setMediumRiskArticles] = useState<number>(0)

    const [arePublisherAnnualMetricsLoading, setAreInstitutionAnnualMetricsLoading] = useState(true)
    const [publisherPublicationHistory, setPublisherPublicationHistory] = useState<{}[]>([]);
    const [drillDownParams, setDrillDownParams] = useState<DrillDownParams>({ startYear: START_YEAR, endYear: END_YEAR, drillDownTitle: "", queryRetractions: true, scitilityRiskScoreLowerBoundary: 0, scitilityRiskScoreHigherBoundary: 1000, journalIds: undefined })
    const [openDrilldown, setOpenDrilldown] = useState<boolean>(false);
    const [openAuthorDrilldown, setOpenAuthorDrilldown] = useState<boolean>(false);
    const [selectedOpenAlexAuthorId, setSelectedOpenAlexAuthorId] = useState<string>();
    const [authorCollaborationMode, setAuthorCollaborationMode] = useState<boolean>(false);
    const [selectedAuthorDisplayName, setSelectedAuthorDisplayName] = useState<string>();
    const [selectedAuthorInstitutionIdAffiliation, setSelectedAuthorInstitutionIdAffiliation] = useState<string>();
    const [selectedAuthorInstitutionNameAffiliation, setSelectedAuthorInstitutionNameAffiliation] = useState<string>();



    async function showHighRisk() {
        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "High Risk Articles between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: false,
            publisherIds: publisherId,
            scitilityRiskScoreLowerBoundary: 1.5,
            journalIds: undefined
        })
        setOpenDrilldown(true)

    }

    async function showMediumRisk() {

        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "Medium Risk Articles between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: false,
            publisherIds: publisherId,
            scitilityRiskScoreLowerBoundary: 0.5,
            scitilityRiskScoreHigherBoundary: 1.5,
            journalIds: undefined
        })
        setOpenDrilldown(true)

    }

    const handleDrilldownClose = () => {
        setOpenDrilldown(false);
    };

    async function showRetractions(publisherId: string) {
        setDrillDownParams({
            startYear: START_YEAR,
            endYear: END_YEAR,
            drillDownTitle: "Retractions between " + START_YEAR + " and " + END_YEAR,
            queryRetractions: true,
            institutionId: undefined,
            publisherIds: publisherId,
            scitilityRiskScoreLowerBoundary: 0
        })
        setOpenDrilldown(true)
    }

    useEffect(() => {

        async function fetchData() {
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(argosApiEntryPoint + '/stats/publisher-summary?publisherId=https://openalex.org/' + publisherId + "&startYear=2014"
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: PublisherAnnualMetricsRequestResult) => {
                    setPublisherPublicationHistory(data.results)
                    let tRetractions = data.results.reduce((previousValue, currentValue) => previousValue + currentValue.retractionsByPublicationYear, 0)
                    let tArticles = data.results.reduce((previousValue, currentValue) => previousValue + currentValue.totalAnalysed, 0)
                    setTotalRetractions(tRetractions);
                    setTotalAnalized(tArticles)
                    setHighRiskArticles(data.results.reduce((previousValue, currentValue) => previousValue + currentValue.numberOfHighRiskArticles, 0));
                    setMediumRiskArticles(data.results.reduce((previousValue, currentValue) => previousValue + currentValue.numberOfMediumRiskArticles, 0));
                    setAreInstitutionAnnualMetricsLoading(false)
                })
        }
        fetchData()
    }, []);

    const yearValueFormatter = (value: number | null) => `${value}`;

    const chartSetting = {
        yAxis: [
            {
                label: 'Number of articles',
            },
        ],
        //width: '80%',        
        height: 600,
        sx: {
            [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-10px, 0)',
            },
            ml: 10,
            mr: 10
        },
    };


    return <>


        <Grid container paddingTop={4}>
            <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                    <Item elevation={4}>
                        <Typography variant="h5" gutterBottom>Retractions since 2014</Typography>
                        {arePublisherAnnualMetricsLoading && <CircularProgress />}
                        {!arePublisherAnnualMetricsLoading && <>
                            <Button onClick={() => showRetractions(publisherId)}>
                                <Typography variant="h2" gutterBottom>{totalRetractions}</Typography>
                            </Button>
                            <Typography>The publisher retraction ratio is {percentage(totalRetractions, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average retraction ratio of 0.1078%, we would have expected {round(totalAnalized * 0.001078)} retractions.</Typography>
                        </>}
                    </Item>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Item elevation={4}>
                        <Typography variant="h5" gutterBottom>High Risk Articles since 2014</Typography>
                        {arePublisherAnnualMetricsLoading && <CircularProgress />}
                        {!arePublisherAnnualMetricsLoading && <>

                            <Button onClick={() => showHighRisk()}>
                                <Typography variant="h2" gutterBottom>{highRiskArticles}</Typography>
                            </Button>

                            <Typography>The publisher high risk ratio is {percentage(highRiskArticles, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average high risk ratio of 0.1507%, we would have expected {round(totalAnalized * 0.001507)} high risk articles.</Typography>
                        </>}
                    </Item>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Item elevation={4}>
                        <Typography variant="h5" gutterBottom>Medium Risk Articles since 2014</Typography>
                        {arePublisherAnnualMetricsLoading && <CircularProgress />}
                        {!arePublisherAnnualMetricsLoading && <>
                            <Button onClick={() => showMediumRisk()}>
                                <Typography variant="h2" gutterBottom>{mediumRiskArticles}</Typography>
                            </Button>
                            <Typography>The publisher medium risk ratio is  {percentage(mediumRiskArticles, totalAnalized)}%. Based on the volume of articles {totalAnalized} and the average medium risk ratio of 0.5572%, we would have expected {round(totalAnalized * 0.005572)} medium risk articles.</Typography>
                        </>}
                    </Item>
                </Grid>
                <Grid item xs={12} md={12}>
                    <Item elevation={4}>
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Publication history showing the article classification
                        </Typography>
                        {arePublisherAnnualMetricsLoading && <CircularProgress />}
                        {!arePublisherAnnualMetricsLoading && <>
                            <BarChart
                                dataset={publisherPublicationHistory}
                                xAxis={[{
                                    label: 'Publication Year',
                                    scaleType: 'band', dataKey: 'publicationYear', valueFormatter: yearValueFormatter
                                }]}
                                series={[
                                    { dataKey: 'numberOfLowRiskArticles', stack: 'history', label: 'Low Risk Articles', color: '#1565C0' },
                                    { dataKey: 'numberOfMediumRiskArticles', stack: 'history', label: 'Medium Risk Articles', color: '#ff6700' },
                                    { dataKey: 'numberOfHighRiskArticles', stack: 'history', label: 'High Risk Articles', color: 'red' },
                                    { dataKey: 'retractions', stack: 'history', label: 'Retractions (by publication year)', color: '#8B0000' }
                                ]}
                                {...chartSetting}
                            /></>}
                    </Item>
                </Grid>
                <Grid item xs={12} md={12}>
                    <JournalSummaryTable mode="publisher" />
                </Grid>
            </Grid>
        </Grid>
        <Dialog
            fullScreen
            open={openDrilldown}
            onClose={handleDrilldownClose}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleDrilldownClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Publisher details
                    </Typography>
                    <Button autoFocus color="inherit" onClick={handleDrilldownClose}>
                        Close
                    </Button>
                </Toolbar>
            </AppBar>
            <DrillDownTable
                journalIds={drillDownParams?.journalIds}
                startYear={drillDownParams?.startYear}
                endYear={drillDownParams?.endYear}
                drillDownTitle={drillDownParams?.drillDownTitle}
                queryRetractions={drillDownParams.queryRetractions}
                scitilityRiskScoreLowerBoundary={drillDownParams.scitilityRiskScoreLowerBoundary}
                scitilityRiskScoreHigherBoundary={drillDownParams.scitilityRiskScoreHigherBoundary}
                publisherIds={drillDownParams.publisherIds}
            />
        </Dialog>
        <RetractionsForOpenAlexAuthorDialog
            openAlexAuthorId={selectedOpenAlexAuthorId!}
            authorDisplayName={selectedAuthorDisplayName!}
            institutionId={selectedAuthorInstitutionIdAffiliation}
            institutionName={selectedAuthorInstitutionNameAffiliation}
            handleClose={() => setOpenAuthorDrilldown(false)}
            openDrilldown={openAuthorDrilldown}
            collaborationMode={authorCollaborationMode}
        />
    </>
}

export default PublisherDashboard